import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Users, UserCheck, Sparkles, BookOpen, ClipboardList, Briefcase,
  CalendarCheck, Megaphone, ArrowUpRight, GraduationCap, FileText
} from "lucide-react";
import { Link } from "react-router-dom";

const Stat = ({ label, value, icon: Icon, accent = "#0F2E4A", testid, suffix }) => (
  <div
    data-testid={testid}
    className="bg-white border border-[#E2E8F0] rounded-lg p-6 hover:shadow-md transition-all"
  >
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] font-semibold">{label}</div>
        <div className="mt-3 font-heading text-3xl font-bold text-[#0F172A]">
          {value}<span className="text-lg text-[#64748B] font-medium">{suffix}</span>
        </div>
      </div>
      <div className="w-10 h-10 rounded-md grid place-items-center" style={{ backgroundColor: `${accent}10` }}>
        <Icon className="w-5 h-5" style={{ color: accent }} strokeWidth={1.75} />
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data));
  }, []);

  if (!data) {
    return <div className="text-[#475569]">Loading dashboard...</div>;
  }

  const isAdmin = data.role !== "student";

  return (
    <div className="space-y-8" data-testid="dashboard-root">
      {/* Hero greeting */}
      <div className="relative overflow-hidden rounded-xl border border-[#E2E8F0] bg-white">
        <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-[#0F2E4A]/5" />
        <div className="absolute right-10 bottom-0 w-40 h-40 rounded-full bg-[#059669]/5" />
        <div className="relative p-8">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#64748B] mb-2">
            {new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
          </div>
          <h1 className="font-heading text-3xl font-bold text-[#0F172A]">
            {isAdmin ? "Operations Overview" : `Hello, ${user.name?.split(" ")[0]}`}
          </h1>
          <p className="text-sm text-[#475569] mt-1 max-w-2xl">
            {isAdmin
              ? "Live view of student activity, admissions, leaves and placement pipeline."
              : `${user.course || "Your"} — Batch ${user.batch || "—"} · Trainer: ${user.trainer || "—"}`}
          </p>
        </div>
      </div>

      {/* Stats grid */}
      {isAdmin ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger">
          <Stat testid="stat-total-students" label="Total Students" value={data.total_students} icon={Users} accent="#0F2E4A" />
          <Stat testid="stat-active-students" label="Active Students" value={data.active_students} icon={UserCheck} accent="#059669" />
          <Stat testid="stat-new-admissions" label="New Admissions (30d)" value={data.new_admissions} icon={Sparkles} accent="#D97706" />
          <Stat testid="stat-total-materials" label="Study Materials" value={data.total_materials} icon={BookOpen} accent="#0F2E4A" />
          <Stat testid="stat-pending-leaves" label="Pending Leaves" value={data.pending_leaves} icon={ClipboardList} accent="#DC2626" />
          <Stat testid="stat-open-jobs" label="Open Job Postings" value={data.open_jobs} icon={Briefcase} accent="#059669" />
          <Stat testid="stat-completed-students" label="Completed Students" value={data.completed_students} icon={GraduationCap} accent="#0F2E4A" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger">
          <Stat testid="stat-attendance" label="Attendance" value={data.attendance_pct} suffix="%" icon={CalendarCheck} accent="#059669" />
          <Stat testid="stat-materials" label="Study Materials" value={data.materials} icon={BookOpen} accent="#0F2E4A" />
          <Stat testid="stat-applications" label="Job Applications" value={data.applications} icon={FileText} accent="#D97706" />
          <Stat testid="stat-scheduled-interviews" label="Scheduled Interviews" value={data.scheduled_interviews} icon={Briefcase} accent="#059669" />
        </div>
      )}

      {/* Recent announcements + batches/quick actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading text-lg font-semibold text-[#0F172A]">Recent Announcements</h3>
              <p className="text-xs text-[#64748B] mt-0.5">Latest notices and updates from the institute</p>
            </div>
            <Link to="/announcements" data-testid="dashboard-view-announcements" className="text-xs text-[#0F2E4A] font-medium flex items-center gap-1 hover:gap-2 transition-all">
              View all <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="space-y-3" data-testid="dashboard-announcements">
            {(data.announcements || []).length === 0 && (
              <div className="text-sm text-[#64748B] py-8 text-center">No announcements yet.</div>
            )}
            {(data.announcements || []).map((a) => (
              <div key={a.id} className="flex gap-4 p-4 rounded-md border border-[#E2E8F0] hover:border-[#0F2E4A]/30 hover:bg-[#F8FAFC] transition-all">
                <div className="w-1 self-stretch rounded-full bg-[#0F2E4A]" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded bg-[#0F2E4A]/5 text-[#0F2E4A]">{a.category}</span>
                    <span className="text-[11px] text-[#64748B]">{new Date(a.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="font-heading font-semibold text-[#0F172A]">{a.title}</div>
                  <div className="text-sm text-[#475569] mt-1 line-clamp-2">{a.body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-lg p-6">
          <h3 className="font-heading text-lg font-semibold text-[#0F172A] mb-4">
            {isAdmin ? "Batch Distribution" : "Your Profile"}
          </h3>
          {isAdmin ? (
            <div className="space-y-3">
              {(data.batch_breakdown || []).length === 0 && <div className="text-sm text-[#64748B]">No batches yet.</div>}
              {(data.batch_breakdown || []).map((b) => (
                <div key={b.batch}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium text-[#0F172A]">{b.batch}</span>
                    <span className="text-[#64748B]">{b.count}</span>
                  </div>
                  <div className="h-1.5 bg-[#F1F5F9] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#0F2E4A] rounded-full"
                      style={{ width: `${Math.min(100, (b.count / Math.max(1, data.total_students)) * 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              <ProfileRow label="Student ID" value={user.student_id} />
              <ProfileRow label="Course" value={user.course} />
              <ProfileRow label="Batch" value={user.batch} />
              <ProfileRow label="Trainer" value={user.trainer} />
              <ProfileRow label="Mobile" value={user.mobile} />
              <ProfileRow label="Status" value={user.status} accent />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProfileRow({ label, value, accent }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-[#F1F5F9] last:border-0">
      <span className="text-[10px] uppercase tracking-wider text-[#64748B] font-semibold">{label}</span>
      <span className={`text-sm font-medium ${accent ? "text-[#059669]" : "text-[#0F172A]"}`}>{value || "—"}</span>
    </div>
  );
}
